/**
 * =============================================================================
 * IMPORTANT, READ THIS MESSAGE BEFORE EDITING THIS FILE.
 *
 * This file needs to be kept tightly in sync with its corresponding version in
 * the backend. If you are changing this file, please also make the
 * corresponding changes in: `cip-backend:functions/src/assignments/types.ts`
 * and submit both PRs simultaneously.
 *
 * Be careful because import paths may be different and the Admin SDK APIs
 * differ from `firebase/firestore`. Don't forget to
 * double check that you update the comments in `cip-backend` as well.
 * =============================================================================
 */
/** ============== COPY EVERYTHING STARTING HERE TO cip-backend ============= */
/** A per-course unique ID for an assignment. */
export type AssignmentId = string;
/** A per-assignment unique ID for a user submission. */
export type SubmissionId = string;

/** The canonical frontend list of supported assignment types. */
export const SUPPORTED_ASSIGNMENT_TYPES = [
  'karel',
  'console',
  'graphics',
  'mathide',
] as const;

/** The frontend list of supported assignment languages. */
export const SUPPORTED_ASSIGNMENT_LANGUAGES = ['python', 'html'] as const;

/** The canonical frontend list of supported autograder types. Should be updated
 *  whenever a new autograder type is added.
 * - `karel`: Karel pre/post world comparison.
 * - `console`: Console input/output comparison.
 * - `advanced_console`: Reactive console test.
 * - `graphics`: Graphics canvas comparison.
 * - `ai`: AI-based evaluation.
 * - `self_graded`: Self-graded.
 * - `none`: No autograder.
 */
export const SUPPORTED_ASSIGNMENT_UNIT_TEST_TYPES = [
  'karel',
  'console',
  'advanced_console',
  'graphics',
  'ai',
  'self_graded',
  'none',
  'function_call',
] as const;

/** The canonical frontend list of supported autograder behaviors. That is, when
 *  to run the autograder for a given assignment.
 * - `on_run`: Run the autograder every time the student runs their code.
 * - `on_check_correct`: Run the autograder when the student checks their code
 *   for correctness with the "Check Correct" button
 */
export const SUPPORTED_ASSIGNMENT_UNIT_TEST_BEHAVIORS = [
  'on_run',
  'on_check_correct',
] as const;

/** The canonical frontend list of supported show solution behaviors. These
 *  behaviors determine when to show the solution in the IDE.
 * - `always`: Always show the solution.
 * - `if_student_solved`: Show the solution if the student has solved the
 *   assignment.
 * - `never`: Never show the solution.
 */
export const SUPPORTED_SHOW_SOLUTION_BEHAVIORS = [
  'always',
  'if_student_solved',
  'never',
] as const;

export const ASSIGNMENT_TYPE_TO_SUPPORTED_UNIT_TEST_TYPES: Record<
  AssignmentType,
  AssignmentUnitTestType[]
> = {
  karel: ['karel', 'self_graded', 'none'],
  console: ['console', 'advanced_console', 'ai', 'self_graded', 'none'],
  graphics: ['graphics', 'ai', 'self_graded', 'none'],
  mathide: ['function_call'],
};

export const ASSIGNMENT_TYPE_TO_DISPLAY_NAME: Record<AssignmentType, string> = {
  karel: 'Karel',
  console: 'Console',
  graphics: 'Graphics',
  mathide: 'Math IDE',
};

export const ASSIGNMENT_LANGUAGE_TO_DISPLAY_NAME: Record<
  AssignmentLanguage,
  string
> = {
  python: 'Python',
  html: 'HTML',
};

export const ASSIGNMENT_UNIT_TEST_TYPE_TO_DISPLAY_NAME: Record<
  AssignmentUnitTestType,
  string
> = {
  karel: 'Karel Pre/Post World Comparison',
  console: 'Console Input/Output Script',
  advanced_console: 'Reactive Console Test',
  graphics: 'Graphics Canvas Match',
  ai: 'AI-based Evaluation',
  self_graded: 'Self-Graded',
  none: 'None',
  function_call: 'Function Call',
};

export const ASSIGNMENT_UNIT_TEST_TOOLTIPS: Record<
  AssignmentUnitTestType,
  string
> = {
  karel:
    'Compare the world state before and after running the student code to the solution.',
  console:
    'Compares a transcript of input/output pairs against the student code.',
  advanced_console:
    'Runs a stateful console test that can react to student input.',
  graphics:
    'Compares the graphics canvas state between the student code and the solution.',
  ai: 'Use an AI model to evaluate the student code.',
  self_graded: 'Allow students to grade themselves.',
  none: 'No autograder.',
  function_call: 'Calls a named Python function with declared args and compares the return value.',
};

export const ASSIGNMENT_UNIT_TEST_BEHAVIOR_TO_DISPLAY_NAME: Record<
  AssignmentUnitTestBehavior,
  string
> = {
  on_run: 'On Run',
  on_check_correct: 'On Check Correct',
};

export const ASSIGNMENT_UNIT_TEST_BEHAVIOR_TOOLTIPS: Record<
  AssignmentUnitTestBehavior,
  string
> = {
  on_run: 'Run the autograder when the student runs their code.',
  on_check_correct:
    'Run the autograder when the student checks their code for correctness.',
};

export const SHOW_SOLUTION_BEHAVIOR_DISPLAY_NAMES: Record<
  ShowSolutionBehavior,
  string
> = {
  always: 'Always',
  if_student_solved: 'If Student Solved',
  never: 'Never',
};

export const SHOW_SOLUTION_BEHAVIOR_TOOLTIPS: Record<
  ShowSolutionBehavior,
  string
> = {
  always: 'Always show the solution (e.g. for a practice assignment).',
  if_student_solved:
    'Show the solution if the student has already solved the assignment.',
  never: 'Never show the solution (e.g. for homework assignments).',
};

// Below is boilerplate code to validate the types at runtime.
export type AssignmentType = (typeof SUPPORTED_ASSIGNMENT_TYPES)[number];
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
export function isAssignmentType(input: any): input is AssignmentType {
  return (
    typeof input === 'string' &&
    SUPPORTED_ASSIGNMENT_TYPES.includes(input as AssignmentType)
  );
}

export type AssignmentLanguage =
  (typeof SUPPORTED_ASSIGNMENT_LANGUAGES)[number];

export type AssignmentUnitTestType =
  (typeof SUPPORTED_ASSIGNMENT_UNIT_TEST_TYPES)[number];
export function isAssignmentUnitTestType(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  input: any,
): input is AssignmentUnitTestType {
  return (
    typeof input === 'string' &&
    SUPPORTED_ASSIGNMENT_UNIT_TEST_TYPES.includes(
      input as AssignmentUnitTestType,
    )
  );
}

export type AssignmentUnitTestBehavior =
  (typeof SUPPORTED_ASSIGNMENT_UNIT_TEST_BEHAVIORS)[number];
export function isUnitTestBehavior(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  input: any,
): input is AssignmentUnitTestBehavior {
  return (
    typeof input === 'string' &&
    SUPPORTED_ASSIGNMENT_UNIT_TEST_BEHAVIORS.includes(
      input as AssignmentUnitTestBehavior,
    )
  );
}

export type ShowSolutionBehavior =
  (typeof SUPPORTED_SHOW_SOLUTION_BEHAVIORS)[number];
export function isShowSolutionBehavior(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  input: any,
): input is ShowSolutionBehavior {
  return (
    typeof input === 'string' &&
    SUPPORTED_SHOW_SOLUTION_BEHAVIORS.includes(input as ShowSolutionBehavior)
  );
}

